import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "@/config/axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Search,
  UserPlus,
  Mail,
  Calendar,
  Trash2,
  Edit2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function AIContacts() {
  const { actorId } = useParams();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  
  // New contact form
  const [newContact, setNewContact] = useState({
    name: "",
    email: "",
    notes: ""
  });

  useEffect(() => {
    if (actorId) {
      fetchContacts();
    }
  }, [actorId]);

  const fetchContacts = async (search = "") => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/api/admin/actors/${actorId}/contacts`,
        {
          params: { search, limit: 100 },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error("Failed to fetch contacts", error);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContacts(searchQuery);
  };

  const createContact = async () => {
    if (!newContact.name || !newContact.email) {
      toast.error("Name and email are required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        `${API}/api/admin/actors/${actorId}/contacts`,
        newContact,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Contact created successfully");
      setShowAddForm(false);
      setNewContact({ name: "", email: "", notes: "" });
      fetchContacts();
    } catch (error) {
      console.error("Failed to create contact", error);
      toast.error(error.response?.data?.detail || "Failed to create contact");
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact?")) return;

    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(
        `${API}/api/admin/actors/${actorId}/contacts/${contactId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Contact deleted");
      fetchContacts();
    } catch (error) {
      console.error("Failed to delete contact", error);
      toast.error("Failed to delete contact");
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/ai/actors/${actorId}/emails`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Emails
          </Button>
          <h1 className="text-2xl font-bold">Contacts</h1>
          <Badge variant="outline">{contacts.length} contacts</Badge>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <UserPlus className="w-4 h-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {/* Add Contact Form */}
      {showAddForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Add New Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  placeholder="John Doe"
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                  placeholder="Optional notes..."
                  value={newContact.notes}
                  onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={createContact}>Create Contact</Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search contacts by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit">Search</Button>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  fetchContacts("");
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Contacts List */}
      {loading ? (
        <div className="text-center py-12">Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              {searchQuery ? "No contacts found matching your search." : "No contacts yet. Add your first contact or sync your emails."}
            </p>
            {!searchQuery && (
              <Button onClick={() => setShowAddForm(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Email
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Source
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      R/S
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Last Contact
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900 truncate max-w-[150px]" title={contact.name}>
                          {contact.name}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-gray-600 truncate max-w-[200px]" title={contact.email}>
                          {contact.email}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Badge variant="outline" className="capitalize text-xs px-1.5 py-0">
                          {contact.source}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-center text-gray-600 whitespace-nowrap">
                        {contact.email_count_received || 0}/{contact.email_count_sent || 0}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {contact.last_contact 
                          ? new Date(contact.last_contact).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                          : "-"
                        }
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-center">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2"
                            onClick={() => navigate(`/ai/actors/${actorId}/emails`, {
                              state: { draftTo: contact.email }
                            })}
                            title="Draft Email"
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2"
                            onClick={() => deleteContact(contact.id)}
                            title="Delete Contact"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
